import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast, Toaster } from "sonner";
import { ArrowRight, Mail, Lock, User as UserIcon, Building2 } from "lucide-react";

export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "register" ? "register" : "login";
  const initialRole = params.get("role") === "dispensary" ? "dispensary" : "consumer";
  const [mode, setMode] = useState(initialMode);
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({ email: "", password: "", name: "", handle: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = mode === "login"
        ? await login(form.email, form.password)
        : await register({ email: form.email, password: form.password, name: form.name, handle: form.handle, role });
      toast.success(`Welcome${u.handle ? ", @" + u.handle : ""}.`);
      nav(u.role === "dispensary" || u.role === "admin" ? "/dashboard" : "/discover");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      <Toaster position="top-right" theme="light" richColors />
      <div className="blob" style={{ width: 480, height: 480, background: "#15FF73", top: -120, left: -100 }} />
      <div className="blob" style={{ width: 380, height: 380, background: "#FF1A8C", bottom: -180, right: -120, animationDelay: "-6s" }} />

      <div className="relative w-full flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <div className="w-9 h-9 bg-[#15FF73] rounded-2xl flex items-center justify-center border-2 border-[#0B0F0C] shadow-[2px_2px_0_#0B0F0C]">
              <span className="font-display text-[#0B0F0C] font-extrabold text-xl leading-none">X</span>
            </div>
            <span className="font-display text-2xl font-bold tracking-tight">X-Seed</span>
          </Link>

          <div className="label-overline mb-3 text-[#FF1A8C]">{mode === "login" ? "Welcome back" : "Sign up free"}</div>
          <h1 className="font-display text-5xl sm:text-6xl font-extrabold mb-3 leading-[0.95] tracking-tight">
            {mode === "login" ? <>Get back to the <span className="text-rainbow">gas.</span></> : <>Join the <span className="text-rainbow">club.</span></>}
          </h1>
          <p className="text-sm text-[#0B0F0C]/60 mb-7">{mode === "login" ? "Pick up your XP where you left off." : "Free. 21+. Earn XP from day one. 🌿"}</p>

          {mode === "register" && (
            <div className="mb-6 grid grid-cols-2 gap-3">
              <button data-testid="role-consumer" onClick={() => setRole("consumer")} className={`card-x p-4 text-left bg-[#FAF6E8] ${role === "consumer" ? "card-green" : ""}`}>
                <UserIcon size={18} strokeWidth={2.4} className={role === "consumer" ? "text-[#15FF73]" : ""} />
                <div className="font-display text-lg font-extrabold mt-2">I smoke</div>
                <div className="text-xs text-[#0B0F0C]/55 mt-1">Find deals</div>
              </button>
              <button data-testid="role-dispensary" onClick={() => setRole("dispensary")} className={`card-x p-4 text-left bg-[#FAF6E8] ${role === "dispensary" ? "card-magenta" : ""}`}>
                <Building2 size={18} strokeWidth={2.4} className={role === "dispensary" ? "text-[#FF1A8C]" : ""} />
                <div className="font-display text-lg font-extrabold mt-2">I sell</div>
                <div className="text-xs text-[#0B0F0C]/55 mt-1">Run a shop</div>
              </button>
            </div>
          )}

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <>
                <Field icon={<UserIcon size={14} strokeWidth={2.4} />} placeholder="Display name (private — encrypted)" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="auth-name" required />
                <Field icon={<UserIcon size={14} strokeWidth={2.4} />} placeholder="Public handle (optional · we'll auto-pick)" value={form.handle} onChange={(v) => setForm({ ...form, handle: v })} testid="auth-handle" />
              </>
            )}
            <Field icon={<Mail size={14} strokeWidth={2.4} />} placeholder="Email (encrypted at rest)" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="auth-email" required />
            <Field icon={<Lock size={14} strokeWidth={2.4} />} placeholder="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="auth-password" required />

            <button type="submit" disabled={loading} data-testid="auth-submit" className="btn-x btn-primary w-full !mt-5">
              {loading ? "..." : mode === "login" ? "Sign in" : "Create account"} {!loading && <ArrowRight size={14} strokeWidth={2.4} />}
            </button>
          </form>
          {mode === "register" && (
            <div className="mt-3 text-[10px] font-mono-x font-bold uppercase tracking-widest text-[#0B0F0C]/50">
              🔒 Your name + email are encrypted at rest. Only your handle shows publicly. <Link to="/privacy" className="underline">Privacy</Link>
            </div>
          )}

          <div className="mt-7 text-sm text-[#0B0F0C]/60">
            {mode === "login" ? (
              <>New here? <button data-testid="switch-to-register" onClick={() => setMode("register")} className="text-[#FF1A8C] font-bold hover:underline">Make an account</button></>
            ) : (
              <>Already in? <button data-testid="switch-to-login" onClick={() => setMode("login")} className="text-[#FF1A8C] font-bold hover:underline">Sign in</button></>
            )}
          </div>

          <div className="mt-8 p-4 rounded-2xl border-2 border-dashed border-[#0B0F0C]/30 bg-[#FFE74C]/20 text-xs leading-relaxed">
            <div className="font-bold mb-1 uppercase tracking-widest text-[10px]">Demo logins</div>
            <div className="font-mono-x">admin@xseed.app / admin1234</div>
            <div className="font-mono-x">owner@greenrose.com / demo1234</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon, testid, onChange, ...rest }) {
  return (
    <label className="flex items-center gap-3 rounded-full border-2 border-[#0B0F0C] bg-[#FAF6E8] focus-within:bg-white focus-within:shadow-[3px_3px_0_#0B0F0C] px-5 h-14 transition-all">
      <span className="text-[#0B0F0C]/60">{icon}</span>
      <input data-testid={testid} onChange={(e) => onChange(e.target.value)} className="bg-transparent flex-1 outline-none text-sm placeholder:text-[#0B0F0C]/45 font-medium" {...rest} />
    </label>
  );
}
